// src/components/Layout.tsx
// ✅ REDESIGN 2025-11-01: qmLEO branding update with improved UX
// 🔄 UPDATE 2025-11-01: Moved user menu from sidebar to header top-right
// 🔄 UPDATE 2025-11-20: Added Purchases navigation
// 🔄 UPDATE 2025-11-26: Added Product Catalog navigation
// CHANGES: New logo, darker sidebar, user menu in header, department info in sidebar

import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { Home, Package, BarChart3, Users, Settings, LogOut, Clipboard, UserPlus, Lock, User, Shield, Building2, ChevronDown, ShoppingCart, BookOpen, PackagePlus, Box, FolderOpen, ClipboardCheck } from 'lucide-react';
import ChangePasswordModal from './auth/ChangePasswordModal';
import qmLeoLogo from '../assets/images/qmLEO_logo.png';

interface LayoutProps {
  userName: string;
  userRole: string;
  tenantId: string;
  organizationName: string;
  onSignOut: () => void;
}

const Layout: React.FC<LayoutProps> = ({ userName, userRole, tenantId, organizationName, onSignOut }) => {
  const location = useLocation();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

 const navigationItems = [
    {
      name: 'Dashboard',
      href: '/',
      icon: Home,
      available: true
    },
   {
      name: 'Product Catalog',
      href: '/products',
      icon: BookOpen,
      available: userRole === 'SuperAdmin' || userRole === 'OrgAdmin'
    },
    {
      name: 'Initial Setup',
      href: '/initial-inventory',
      icon: PackagePlus,
      available: userRole === 'SuperAdmin' || userRole === 'OrgAdmin'
    },
    {
      name: 'Purchases',
      href: '/purchases',
      icon: ShoppingCart,
      available: userRole === 'SuperAdmin' || userRole === 'OrgAdmin' || userRole === 'InventoryManager'
    },
    {
      name: 'Inventory',
      href: '/inventory',
      icon: Package,
      available: true
    },
    {
      name: 'Assignments',
      href: '/assignments',
      icon: Clipboard,
      available: userRole === 'SuperAdmin' || userRole === 'OrgAdmin' || userRole === 'InventoryManager'
    },
    {
      name: 'Onboarding',
      href: '/onboarding',
      icon: UserPlus,
      available: userRole === 'SuperAdmin' || userRole === 'OrgAdmin' || userRole === 'InventoryManager'
    },
    {
      name: 'Reports',
      href: '/reports',
      icon: BarChart3,
      available: true
    },
   {
      name: 'Assets',
      href: '/assets',
      icon: Box,
      available: (userRole === 'SuperAdmin' || userRole === 'OrgAdmin' || userRole === 'InventoryManager') && 
                 (() => {
                   try {
                     const features = JSON.parse(localStorage.getItem('qm_tenantFeatures') || '[]');
                     return features.includes('ASSET_TRACKING');
                   } catch {
                     return false;
                   }
                 })()
    },
    {
      name: 'Users',
      href: '/users',
      icon: Users,
      available: userRole === 'SuperAdmin' || userRole === 'OrgAdmin' || userRole === 'InventoryManager'
    },
    {
      name: 'System Access',
      href: '/system-access',
      icon: Shield,
      available: userRole === 'SuperAdmin' || userRole === 'OrgAdmin'
    },
    {
      name: 'Audit Log',
      href: '/audit',
      icon: Shield,
      available: userRole === 'SuperAdmin' || userRole === 'OrgAdmin'
    },
    {
      name: 'Admin',
      href: '/admin',
      icon: Settings,
      available: userRole === 'SuperAdmin' || userRole === 'OrgAdmin'
    }
  ];

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const handleChangePasswordSuccess = () => {
    console.log('Password changed successfully');
  };

  // Close user menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.user-menu-container')) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showUserMenu]);

  // Role badge styling helper
  const getRoleBadgeStyle = (role: string) => {
    switch (role) {
      case 'SuperAdmin':
        return 'bg-purple-100 text-purple-800';
      case 'OrgAdmin':
        return 'bg-blue-100 text-blue-800';
      case 'InventoryManager':
        return 'bg-emerald-100 text-emerald-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <>
      <div className="min-h-screen bg-gray-50 flex">
        {/* Sidebar - Enhanced with qmLEO branding */}
        <div className="w-64 bg-gradient-to-b from-[#0d1b2a] to-[#1b263b] shadow-2xl">
          {/* Logo/Header - qmLEO branding */}
          <div className="p-6 border-b border-gray-700/50 flex justify-center items-center bg-[#0d1b2a]">
            <img 
              src={qmLeoLogo} 
              alt="qmLEO" 
              className="h-auto w-auto"
            />
          </div>

          {/* Navigation */}
          <nav className="mt-2 px-2">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              
              if (!item.available) {
                return (
                  <div
                    key={item.name}
                    className="flex items-center px-4 py-3 my-1 text-sm text-gray-500 cursor-not-allowed rounded-lg"
                  >
                    <Icon className="w-5 h-5 mr-3" />
                    {item.name}
                  </div>
                );
              }

              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center px-4 py-3 my-1 text-sm font-medium transition-all duration-200 rounded-lg ${
                    isActive(item.href)
                      ? 'text-white bg-blue-600 shadow-lg shadow-blue-900/50'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                  }`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </Link>
              );
            })}
          </nav>

          {/* Department/Tenant Information Display - Bottom Left */}
          <div className="absolute bottom-0 w-64 border-t border-gray-700/50">
            <div className="px-4 py-4 bg-blue-900/20">
              <div className="flex items-center">
                <Building2 className="w-5 h-5 text-blue-400 mr-3 flex-shrink-0" />
                <div className="text-left min-w-0 flex-1">
                  <p className="text-sm font-semibold text-blue-100 truncate" title={organizationName}>
                    {organizationName}
                  </p>
                  <p className="text-xs text-blue-300 mt-0.5">
                    {tenantId === 'SYSTEM' ? 'System Admin' : 'Department Portal'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Top Bar - With User Menu in Top Right */}
          <header className="bg-white shadow-sm border-b border-gray-200 px-6 py-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900">
                {navigationItems.find(item => isActive(item.href))?.name || 'Dashboard'}
              </h2>

              {/* User Menu - Top Right */}
              <div className="user-menu-container relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="w-9 h-9 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-md flex-shrink-0">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900" title={userName}>
                      {userName}
                    </p>
                    <span className={`inline-block text-xs px-2 py-0.5 rounded-full font-medium ${getRoleBadgeStyle(userRole)}`}>
                      {userRole}
                    </span>
                  </div>
                  <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform flex-shrink-0 ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* User Menu Dropdown */}
                {showUserMenu && (
                  <div className="absolute right-0 top-full mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-50">
                    <div className="py-2">
                      <button
                        onClick={() => {
                          setShowChangePassword(true);
                          setShowUserMenu(false);
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center transition-colors"
                      >
                        <Lock className="w-4 h-4 mr-3 text-gray-500" />
                        Change Password
                      </button>
                      <hr className="my-1 border-gray-200" />
                      <button
                        onClick={() => {
                          onSignOut();
                          setShowUserMenu(false);
                        }}
                        className="w-full px-4 py-2.5 text-left text-sm text-red-600 hover:bg-red-50 flex items-center transition-colors"
                      >
                        <LogOut className="w-4 h-4 mr-3" />
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 p-6 bg-gray-50 overflow-auto">
            <Outlet />
          </main>
        </div>
      </div>

      {/* Change Password Modal */}
      <ChangePasswordModal
        isOpen={showChangePassword}
        onClose={() => setShowChangePassword(false)}
        onSuccess={handleChangePasswordSuccess}
      />
    </>
  );
};

export default Layout;
